import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { imgUrl } from "@/lib/api";

function Thumb({ item }) {
  const [ok, setOk] = useState(true);
  return (
    <div className="relative h-20 w-16 shrink-0 border-2 border-black bg-black overflow-hidden">
      {ok ? (
        <img src={imgUrl(item.image)} alt={item.name} onError={() => setOk(false)} className="h-full w-full object-cover" />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center text-center font-display text-[9px] leading-tight text-background p-1 uppercase">
          {item.slogan}
        </span>
      )}
    </div>
  );
}

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" data-testid="cart-drawer" className="w-full sm:max-w-md bg-background border-l-2 border-black p-0 flex flex-col rounded-none">
        <SheetHeader className="border-b-2 border-black p-5 text-left">
          <SheetTitle className="font-display uppercase text-2xl tracking-tight">Your Haul</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 && (
            <p className="font-mono text-sm text-muted-foreground mt-8" data-testid="cart-empty">
              Nothing in here but good intentions.
            </p>
          )}
          {items.map((item) => (
            <div key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-4 border-2 border-black p-3 shadow-hard bg-background">
              <Thumb item={item} />
              <div className="flex-1 min-w-0">
                <p className="font-display uppercase leading-tight truncate">{item.name}</p>
                <p className="font-mono text-xs text-muted-foreground uppercase mt-1">
                  {item.color} · Size {item.size} · ${item.price.toFixed(2)}
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <button
                    data-testid={`qty-minus-${item.key}`}
                    aria-label={`Decrease quantity of ${item.name}`}
                    onClick={() => updateQty(item.key, -1)}
                    className="h-7 w-7 border-2 border-black flex items-center justify-center hover:bg-black hover:text-background transition-colors"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="font-mono text-sm w-4 text-center">{item.qty}</span>
                  <button
                    data-testid={`qty-plus-${item.key}`}
                    aria-label={`Increase quantity of ${item.name}`}
                    onClick={() => updateQty(item.key, 1)}
                    className="h-7 w-7 border-2 border-black flex items-center justify-center hover:bg-black hover:text-background transition-colors"
                  >
                    <Plus size={12} />
                  </button>
                  <button
                    data-testid={`remove-${item.key}`}
                    aria-label={`Remove ${item.name} from cart`}
                    onClick={() => removeItem(item.key)}
                    className="ml-auto text-muted-foreground hover:text-primary transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
              <p className="font-mono text-sm font-bold">${(item.price * item.qty).toFixed(2)}</p>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="border-t-2 border-black p-5 space-y-4 bg-background">
            <div className="flex justify-between font-mono text-sm uppercase">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal" className="font-bold">${subtotal.toFixed(2)}</span>
            </div>
            <p data-testid="free-shipping-note" className="border-2 border-black bg-primary px-3 py-2 font-mono text-xs font-bold uppercase tracking-wide text-white">
              First item ships free $65+ ($4.95 under) · +$1.95 per extra item
            </p>
            <button
              data-testid="cart-checkout-button"
              onClick={() => {
                setIsOpen(false);
                navigate("/checkout");
              }}
              className="w-full border-2 border-black bg-primary py-4 font-display uppercase text-xl text-white shadow-hard btn-press"
            >
              Checkout
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
